import { useMemo, useState } from 'react';

import { PageShell } from '@/components/layout/page-shell';
import { AssignmentRow } from '@/components/feature/assignment-row';
import { AssignmentDetailSheet } from '@/components/feature/assignment-detail-sheet';
import { useAssignments } from '@/hooks/use-assignments';
import type { AssignmentListItem, AssignmentStatus } from '@/types/api';
import { cls } from '@/lib/format';

const tabs: { key: AssignmentStatus | 'pending'; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'awaiting_approval', label: 'Review' },
  { key: 'completed', label: 'Done' },
];

export default function AssignmentsPage() {
  const [status, setStatus] = useState<AssignmentStatus>('pending');
  const [activeID, setActiveID] = useState<string | null>(null);
  const { data, isLoading } = useAssignments(status);

  const list = useMemo(() => data ?? [], [data]);
  const active: AssignmentListItem | undefined = list.find((a) => a.id === activeID);

  return (
    <PageShell title="Assignments">
      <div className="sticky top-0 z-10 bg-[var(--color-bg)] px-4 pb-2 pt-1">
        <div className="flex gap-1 rounded-xl bg-[var(--color-secondary-bg)] p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatus(t.key as AssignmentStatus)}
              className={cls(
                'flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity',
                status === t.key
                  ? 'bg-[var(--color-bg)] text-[var(--color-fg)] shadow-sm'
                  : 'text-[var(--color-muted)]',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 px-4 py-2">
        {isLoading ? (
          <p className="py-10 text-center text-sm text-[var(--color-muted)]">Loading…</p>
        ) : list.length === 0 ? (
          <p className="rounded-xl bg-[var(--color-secondary-bg)] px-4 py-8 text-center text-sm text-[var(--color-muted)]">
            No assignments here.
          </p>
        ) : (
          list.map((a) => (
            <AssignmentRow
              key={a.id}
              assignment={a}
              onClick={() => setActiveID(a.id)}
            />
          ))
        )}
      </div>

      {active ? (
        <AssignmentDetailSheet
          assignment={active}
          onClose={() => setActiveID(null)}
        />
      ) : null}
    </PageShell>
  );
}
